// RITA-1543 — Echte Werte aller Platzhalter eines Vertragsdokuments für einen Berater.
//
// Ein gemeinsamer Baustein für die Vorschau bei „Vertrag erstellen“ und später für das PDF (3c, POST
// /contract-templates/pdf mit denselben `values`). Die Werte des gemeinsamen Katalogs kommen
// unverändert aus `advisorPlaceholderValues` (Systemzugangs-Mails); hier kommen die Schlüssel dazu,
// die es nur im Vertragseditor gibt, und zwei Werte, die die Mails bewusst leer lassen:
// TEAMLEITER (Vor- und Nachname des Teamleiters; Julia 19.10.2026, Skizze F6) und MYJACK_UNIT (Feld
// „myJACK Unit“ unter Systeme; Julia 19.10.2026 nach Korrektur der Skizze F6, die „bleibt leer, wird im PRM
// nicht gepflegt“ vorsah, weil das Feld übersehen war). Fehlende Angaben bleiben leer (RITA-901).
import {
  AdvisorPlaceholderContext,
  AdvisorTemplateValues,
  advisorPlaceholderValues,
} from '../../templates/services/renderAdvisorTemplate';
import { VertragPlatzhalterKey } from '../../templates/contractEditor/model/platzhalterkatalog';

export interface VertragBeraterwerte extends AdvisorTemplateValues {
  /** Feld „myJACK Unit“ (Bereich Systeme). */
  jackplus_id?: number | string | null;
}

export interface Bearbeitername {
  vorname: string | null;
  nachname: string | null;
}

export interface VertragWertekontext extends AdvisorPlaceholderContext {
  /** Vor- und Nachname des Teamleiters; leer, wenn keiner eingetragen ist. */
  teamleiterName: string;
  /** Angemeldete Person aus Einstellungen › Benutzer. */
  bearbeiter: Bearbeitername;
  /** Tag der Vorschau (Platzhalter HEUTIGES_DATUM). */
  heute: Date;
}

const DATUM_LANG = new Intl.DateTimeFormat('de-DE', {
  day: '2-digit',
  month: 'long',
  year: 'numeric',
  timeZone: 'Europe/Berlin',
});

/** Heutiges Datum wie in RITA-1535 vereinbart, z. B. „08. Oktober 2026“ (Kalendertag in Berlin). */
export function heutigesDatum(heute: Date): string {
  return DATUM_LANG.format(heute);
}

/** „Liebe Anna“ / „Lieber Max“; ohne Geschlecht oder Vornamen bleibt die Anrede leer. */
export function persoenlicheAnrede(isMale: number | null | undefined, vorname: string | null | undefined): string {
  const name = (vorname ?? '').trim();
  if (!name) return '';
  if (isMale === 1) return `Lieber ${name}`;
  if (isMale === 0) return `Liebe ${name}`;
  return '';
}

/** Vor- und Nachname, soweit eingetragen. */
export function vollerName(vorname: string | null | undefined, nachname: string | null | undefined): string {
  return [vorname, nachname]
    .map((teil) => (teil ?? '').trim())
    .filter(Boolean)
    .join(' ');
}

/** Name der angemeldeten Person; kein Rückgriff auf den Benutzernamen. */
export function bearbeiterText(bearbeiter: Bearbeitername): string {
  return vollerName(bearbeiter.vorname, bearbeiter.nachname);
}

/** Wie die Maske (`values.jackplus_id || ''`): 0 und leer gelten als nicht eingetragen. */
function myjackUnit(wert: number | string | null | undefined): string {
  return String(wert || '').trim();
}

interface Eintrag {
  id: number | string;
  title: string;
}

/** Die in der App geladenen Stammdatenlisten (DataContext), aus denen die Namen kommen. */
export interface Stammdatenlisten {
  firmen: Eintrag[];
  laender: Eintrag[];
  status: Eintrag[];
  raenge: Eintrag[];
  level: Eintrag[];
  vertragsarten: Eintrag[];
  berater: Array<{ id: number | string; name?: string | null; surname?: string | null }>;
}

export interface StammdatenBezug extends VertragBeraterwerte {
  manager_id?: number | string | null;
}

const titelZu = (liste: Eintrag[], id: number | string | null | undefined): string =>
  id === null || id === undefined ? '' : (liste.find((eintrag) => String(eintrag.id) === String(id))?.title ?? '');

/**
 * Namen zu den ids des Beraters – wie im Fenster „Systemzugänge › Beantragen“. Teamleiter: Vor- und
 * Nachname des eingetragenen Teamleiters (Julia 19.10.2026, F6); die Maske zeigt nur den Nachnamen.
 */
export function stammdatenKontext(
  values: StammdatenBezug,
  listen: Stammdatenlisten,
): Omit<VertragWertekontext, 'bearbeiter' | 'heute'> {
  const teamleiter =
    values.manager_id === null || values.manager_id === undefined || values.manager_id === ''
      ? undefined
      : listen.berater.find((berater) => String(berater.id) === String(values.manager_id));
  return {
    officeTitle: titelZu(listen.firmen, values.branch_office_id) || values.branch_office?.title || '',
    countryTitle: titelZu(listen.laender, values.country_id) || values.country?.title || '',
    statusTitle: titelZu(listen.status, values.status_id),
    rankTitle: titelZu(listen.raenge, values.rank_id),
    levelTitle: titelZu(listen.level, values.level_id),
    contractTypeName: titelZu(listen.vertragsarten, values.contract_type_id),
    country2Title: titelZu(listen.laender, values.country2_id),
    teamleiterName: teamleiter ? vollerName(teamleiter.name, teamleiter.surname) : '',
  };
}

export function vertragPlatzhalterwerte(
  values: VertragBeraterwerte,
  kontext: VertragWertekontext,
): Record<VertragPlatzhalterKey, string> {
  const beraterwerte = advisorPlaceholderValues(values, kontext);
  return {
    ...beraterwerte,
    VERTRAGSPARTNER:
      values.business_owner?.trim() ||
      [beraterwerte.ANREDE, vollerName(values.name, values.surname)].filter(Boolean).join(' '),
    TEAMLEITER: kontext.teamleiterName.trim(),
    MYJACK_UNIT: myjackUnit(values.jackplus_id),
    PERSOENLICHE_ANREDE: persoenlicheAnrede(values.is_male, values.name),
    BEARBEITER: bearbeiterText(kontext.bearbeiter),
    HEUTIGES_DATUM: heutigesDatum(kontext.heute),
  };
}
